import { motion } from "framer-motion";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useCookieConsent } from "@/hooks/use-cookie-consent";

const statusLabel = (enabled: boolean) => (enabled ? "aktiviert" : "deaktiviert");

const Cookies = () => {
  const {
    consent,
    hasAnswered,
    preferences,
    acceptAll,
    rejectOptional,
    openPreferences,
  } = useCookieConsent();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-32 pb-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(210_100%_15%)_0%,transparent_60%)] opacity-60" />
          <div className="container relative mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <span className="mb-4 block text-sm uppercase tracking-[0.3em] text-primary">
                Datenschutz
              </span>
              <h1 className="font-display text-5xl font-bold md:text-6xl">
                Cookie-<span className="text-primary">Einstellungen</span>
              </h1>
              <p className="mt-4 text-sm text-muted-foreground">
                Stand: 16. April 2026
              </p>
            </motion.div>
          </div>
        </section>

        <section className="pb-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-4xl space-y-6 border border-border bg-card p-6 md:p-10"
            >
            <div className="space-y-3">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
                Aktueller Status
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Diese Website nutzt derzeit keine Analytics- oder Marketing-Cookies. Optional ist
                nur die eingebettete Google-Maps-Karte. Ihre Auswahl wird lokal gespeichert, damit
                die Seite Ihre Entscheidung respektiert.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-sm border border-border bg-background/60 p-4">
                  <p className="font-display text-sm uppercase tracking-[0.16em] text-primary">
                    Entscheidung
                  </p>
                  <p className="mt-2 text-lg text-foreground">
                    {hasAnswered
                      ? `Gespeichert am ${new Date(consent!.decidedAt).toLocaleString("de-DE", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}`
                      : "Noch keine Auswahl gespeichert"}
                  </p>
                </div>
                <div className="rounded-sm border border-border bg-background/60 p-4">
                  <p className="font-display text-sm uppercase tracking-[0.16em] text-primary">
                    Externe Medien
                  </p>
                  <p className="mt-2 text-lg text-foreground">
                    {statusLabel(preferences.externalMedia)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
                Eingesetzte Kategorien
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-sm border border-border bg-background/60 p-4">
                  <p className="font-display text-lg font-bold uppercase tracking-wide text-foreground">
                    Notwendig
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Speichert ausschließlich Ihre Consent-Entscheidung lokal im Browser. Ohne diese
                    Speicherung müssten wir Sie bei jedem Seitenaufruf erneut fragen.
                  </p>
                </div>
                <div className="rounded-sm border border-border bg-background/60 p-4">
                  <p className="font-display text-lg font-bold uppercase tracking-wide text-foreground">
                    Externe Medien
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Betrifft aktuell nur Google Maps. Die Karte wird erst nach Einwilligung geladen;
                    dabei kann Google insbesondere Ihre IP-Adresse und technische Nutzungsdaten
                    erhalten.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
                Auswahl ändern
              </h2>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={openPreferences}
                  className="rounded-sm border border-border px-5 py-3 text-sm font-display uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-secondary"
                >
                  Einstellungen öffnen
                </button>
                <button
                  type="button"
                  onClick={rejectOptional}
                  className="rounded-sm border border-border px-5 py-3 text-sm font-display uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-secondary"
                >
                  Optionale Dienste ablehnen
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-sm border border-primary/50 bg-primary/10 px-5 py-3 text-sm font-display uppercase tracking-[0.14em] text-primary transition-colors hover:bg-primary/20"
                >
                  Optionale Dienste erlauben
                </button>
              </div>
            </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Cookies;
